import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
    GraduationCap,
    Sparkles,
    Megaphone,
    Users as UsersIcon,
    Trophy,
    Award,
    TrendingUp,
    TrendingDown,
    Coins,
    Clock,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import {
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    Cell,
    CartesianGrid,
    Line,
    ComposedChart,
} from "recharts";
import Layout from "../components/Layout";
import api from "../lib/api";

const SOURCE_META = {
    quiz: { label: "Quiz", icon: GraduationCap, color: "text-shariti-orange", hex: "#EE722E" },
    quiz_bonus: { label: "Bonus quiz", icon: Sparkles, color: "text-shariti-yellow", hex: "#FCDB53" },
    association_quiz: { label: "Quiz association", icon: GraduationCap, color: "text-shariti-orange-light", hex: "#F89F63" },
    onboarding: { label: "Onboarding", icon: Award, color: "text-shariti-yellow", hex: "#FCDB53" },
    simulator: { label: "Simulateur IA", icon: Sparkles, color: "text-shariti-orange", hex: "#EE722E" },
    social_action: { label: "Engagement social", icon: Megaphone, color: "text-shariti-orange", hex: "#EE722E" },
    social_invalidation: { label: "Invalidation", icon: TrendingDown, color: "text-red-400", hex: "#f87171" },
    cooptation_signup: { label: "Cooptation (inscription)", icon: UsersIcon, color: "text-emerald-400", hex: "#34d399" },
    cooptation_approval: { label: "Cooptation (validation)", icon: UsersIcon, color: "text-emerald-400", hex: "#34d399" },
    weekly_challenge: { label: "Challenge hebdo", icon: Trophy, color: "text-shariti-yellow", hex: "#FCDB53" },
    admin_adjust: { label: "Ajustement admin", icon: Award, color: "text-white/60", hex: "#9ca3af" },
};

function fmtDateTime(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("fr-FR", {
            day: "numeric",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch (e) {
        return "—";
    }
}

// Mobile-friendly compact date: "3 mai · 16:33" (no year, no leading zeros)
function fmtDateTimeShort(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        const date = d.toLocaleDateString("fr-FR", {
            day: "numeric",
            month: "short",
        });
        const time = d.toLocaleTimeString("fr-FR", {
            hour: "2-digit",
            minute: "2-digit",
        });
        return `${date} · ${time}`;
    } catch (e) {
        return "—";
    }
}

function fmtMonth(iso) {
    if (!iso) return "—";
    try {
        const [y, m] = iso.split("-");
        const d = new Date(Number(y), Number(m) - 1, 1);
        return d.toLocaleDateString("fr-FR", { month: "short", year: "2-digit" });
    } catch (e) {
        return iso;
    }
}

function fmtMonthLong(iso) {
    if (!iso) return "—";
    try {
        const [y, m] = iso.split("-");
        const d = new Date(Number(y), Number(m) - 1, 1);
        return d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
    } catch (e) {
        return iso;
    }
}

// Rich tooltip — shows total + count + top 3 sources for the hovered month.
function ChartTooltip({ active, payload }) {
    if (!active || !payload?.length) return null;
    const m = payload[0]?.payload;
    if (!m) return null;
    const top = Object.entries(m.by_source || {})
        .sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]))
        .slice(0, 4);
    const positive = m.delta >= 0;
    return (
        <div
            className="rounded-2xl border border-white/10 bg-black/90 backdrop-blur-xl shadow-2xl px-4 py-3 min-w-[220px]"
            data-testid="chart-tooltip"
        >
            <p className="text-[10px] uppercase tracking-[0.25em] font-thin text-shariti-orange-light mb-1">
                {fmtMonthLong(m.month)}
            </p>
            <div className="flex items-baseline gap-2 mb-2">
                <span
                    className={`text-2xl font-extrabold tabular-nums ${
                        positive ? "text-shariti-orange-light" : "text-red-400"
                    }`}
                >
                    {positive ? "+" : ""}
                    {m.delta}
                </span>
                <span className="text-xs text-white/40 font-bold uppercase tracking-widest">
                    pts
                </span>
            </div>
            <p className="text-[11px] text-white/50 mb-2">
                {m.count} transaction{m.count > 1 ? "s" : ""}
            </p>
            {top.length > 0 && (
                <div className="border-t border-white/10 pt-2 space-y-1">
                    {top.map(([src, amount]) => {
                        const meta = SOURCE_META[src] || {
                            label: src,
                            icon: Award,
                            hex: "#9ca3af",
                        };
                        const Icon = meta.icon;
                        return (
                            <div
                                key={src}
                                className="flex items-center justify-between gap-3 text-[11px]"
                            >
                                <span className="inline-flex items-center gap-1.5 text-white/70">
                                    <Icon
                                        className="w-3 h-3 shrink-0"
                                        style={{ color: meta.hex }}
                                    />
                                    {meta.label}
                                </span>
                                <span
                                    className="font-extrabold tabular-nums"
                                    style={{ color: meta.hex }}
                                >
                                    {amount >= 0 ? "+" : ""}
                                    {amount}
                                </span>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default function PointsHistory() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [filter, setFilter] = useState("all");
    const [rewards, setRewards] = useState(null);

    useEffect(() => {
        api.get("/me/points/history").then((r) => setData(r.data));
        api.get("/rewards/settings").then((r) => setRewards(r.data));
    }, []);

    const filtered = useMemo(() => {
        if (!data) return [];
        if (filter === "all") return data.transactions;
        return data.transactions.filter((t) => t.source === filter);
    }, [data, filter]);

    const totalEur = useMemo(() => {
        if (!rewards?.cash_visible || !data) return null;
        return (data.total * (rewards.cash_rate_eur_per_point || 0)).toLocaleString("fr-FR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    }, [data, rewards]);

    if (!data) {
        return (
            <Layout title="Historique" subtitle="Détail de ton score">
                <div className="h-32 glass-card rounded-3xl animate-pulse" />
            </Layout>
        );
    }

    return (
        <Layout title="Historique" subtitle="Comment ton total a été construit">
            <div className="space-y-6" data-testid="points-history-page">
                {/* Hero */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="glass-card rounded-3xl p-5 lg:p-6 border border-shariti-orange/20 grid lg:grid-cols-[auto_1fr] gap-6 items-center"
                >
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-2xl bg-shariti-orange/15 flex items-center justify-center shrink-0">
                            <Award className="w-7 h-7 text-shariti-orange" />
                        </div>
                        <div>
                            <p className="text-[10px] font-thin tracking-[0.3em] uppercase text-shariti-orange-light">
                                Total cumulé
                            </p>
                            <p className="text-4xl lg:text-5xl font-extrabold text-white tabular-nums">
                                {data.total.toLocaleString("fr-FR")}
                                <span className="text-lg text-white/40 font-bold ml-2">pts</span>
                            </p>
                        </div>
                    </div>
                    {totalEur != null && (
                        <div
                            className="lg:text-right text-left"
                            data-testid="points-history-cash-equiv"
                        >
                            <p className="text-[10px] font-thin tracking-[0.3em] uppercase text-shariti-yellow mb-1 flex items-center gap-1 lg:justify-end">
                                <Coins className="w-3 h-3" />
                                Estimation en euros
                            </p>
                            <p className="text-3xl font-extrabold text-shariti-yellow">
                                {totalEur} €
                            </p>
                            <p className="text-[10px] text-white/40 mt-1">
                                Sur la base de {rewards.cash_rate_eur_per_point} €/pt
                                — estimation indicative.
                            </p>
                            {user?.cash_eligible && (
                                <Link
                                    to="/paiements"
                                    data-testid="points-history-go-payouts"
                                    className="mt-3 inline-flex items-center gap-1.5 text-xs font-extrabold text-emerald-300 hover:text-emerald-200 underline-offset-4 hover:underline"
                                >
                                    Réclamer mes paiements
                                    <span aria-hidden>→</span>
                                </Link>
                            )}
                        </div>
                    )}
                </motion.div>

                {/* Monthly chart */}
                {data.monthly_series.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="glass-card rounded-3xl p-5"
                        data-testid="points-history-chart"
                    >
                        <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
                            <div>
                                <p className="text-[10px] font-thin tracking-[0.3em] uppercase text-shariti-orange-light">
                                    Évolution mensuelle
                                </p>
                                <p className="text-base font-extrabold text-white">
                                    12 derniers mois ·{" "}
                                    <span className="text-shariti-orange-light">
                                        {data.monthly_series.reduce(
                                            (a, m) => a + (m.delta || 0),
                                            0,
                                        )}{" "}
                                        pts cumulés
                                    </span>
                                </p>
                            </div>
                            <p className="text-[10px] text-white/40 italic">
                                Touche / survole une barre pour voir le détail par source
                            </p>
                        </div>
                        <div style={{ width: "100%", height: 240 }}>
                            <ResponsiveContainer>
                                <ComposedChart
                                    data={(() => {
                                        // Build cumulative running balance from monthly deltas (chronological)
                                        let bal = 0;
                                        return data.monthly_series.map((m) => {
                                            bal += m.delta || 0;
                                            return {
                                                ...m,
                                                label: fmtMonth(m.month),
                                                cumulative: bal,
                                            };
                                        });
                                    })()}
                                    margin={{ top: 8, right: 4, left: -16, bottom: 0 }}
                                >
                                    <defs>
                                        <linearGradient
                                            id="grad-bar"
                                            x1="0"
                                            y1="0"
                                            x2="0"
                                            y2="1"
                                        >
                                            <stop offset="0%" stopColor="#FCDB53" stopOpacity={0.95} />
                                            <stop offset="100%" stopColor="#EE722E" stopOpacity={0.85} />
                                        </linearGradient>
                                        <linearGradient
                                            id="grad-bar-neg"
                                            x1="0"
                                            y1="0"
                                            x2="0"
                                            y2="1"
                                        >
                                            <stop offset="0%" stopColor="#dc2626" stopOpacity={0.9} />
                                            <stop offset="100%" stopColor="#7c1d1d" stopOpacity={0.85} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid
                                        strokeDasharray="3 3"
                                        stroke="rgba(255,255,255,0.05)"
                                        vertical={false}
                                    />
                                    <XAxis
                                        dataKey="label"
                                        tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 10 }}
                                        axisLine={false}
                                        tickLine={false}
                                    />
                                    <YAxis
                                        yAxisId="left"
                                        tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }}
                                        axisLine={false}
                                        tickLine={false}
                                    />
                                    <YAxis
                                        yAxisId="right"
                                        orientation="right"
                                        tick={{ fill: "rgba(252,219,83,0.45)", fontSize: 10 }}
                                        axisLine={false}
                                        tickLine={false}
                                    />
                                    <Tooltip
                                        cursor={{ fill: "rgba(238,114,46,0.08)" }}
                                        content={<ChartTooltip />}
                                    />
                                    <Bar
                                        yAxisId="left"
                                        dataKey="delta"
                                        radius={[8, 8, 0, 0]}
                                        maxBarSize={42}
                                    >
                                        {data.monthly_series.map((m, i) => (
                                            <Cell
                                                key={i}
                                                fill={
                                                    m.delta >= 0
                                                        ? "url(#grad-bar)"
                                                        : "url(#grad-bar-neg)"
                                                }
                                            />
                                        ))}
                                    </Bar>
                                    <Line
                                        yAxisId="right"
                                        type="monotone"
                                        dataKey="cumulative"
                                        stroke="#FCDB53"
                                        strokeWidth={2}
                                        dot={{ r: 3, fill: "#FCDB53", strokeWidth: 0 }}
                                        activeDot={{
                                            r: 5,
                                            fill: "#FCDB53",
                                            stroke: "#181818",
                                            strokeWidth: 2,
                                        }}
                                    />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="flex items-center gap-4 mt-3 text-[10px] text-white/45">
                            <span className="inline-flex items-center gap-1.5">
                                <span className="inline-block w-3 h-3 rounded-sm bg-gradient-to-b from-shariti-yellow to-shariti-orange" />
                                Gain mensuel
                            </span>
                            <span className="inline-flex items-center gap-1.5">
                                <span className="inline-block w-3 h-0.5 bg-shariti-yellow" />
                                Solde cumulé
                            </span>
                        </div>
                    </motion.div>
                )}

                {/* Source filters */}
                <div className="overflow-x-auto scrollbar-hide -mx-1 px-1">
                    <div
                        className="flex gap-2 p-1.5 glass-card rounded-full w-max"
                        data-testid="points-history-filters"
                    >
                        <FilterChip
                            label="Tout"
                            active={filter === "all"}
                            onClick={() => setFilter("all")}
                            count={data.transactions.length}
                            testid="filter-all"
                        />
                        {data.sources.map((s) => (
                            <FilterChip
                                key={s}
                                label={SOURCE_META[s]?.label || s}
                                active={filter === s}
                                onClick={() => setFilter(s)}
                                count={data.transactions.filter((t) => t.source === s).length}
                                testid={`filter-${s}`}
                            />
                        ))}
                    </div>
                </div>

                {/* Transactions */}
                {filtered.length === 0 ? (
                    <div
                        className="glass-card rounded-3xl p-8 text-center"
                        data-testid="points-history-empty"
                    >
                        <Award className="w-10 h-10 text-white/30 mx-auto mb-3" />
                        <p className="text-white/60">
                            {data.transactions.length === 0
                                ? "Aucune transaction enregistrée pour le moment. Tes prochaines actions seront tracées ici."
                                : "Aucune transaction pour ce filtre."}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {filtered.map((tx) => {
                            const meta = SOURCE_META[tx.source] || {
                                label: tx.source,
                                icon: Award,
                                color: "text-white/60",
                            };
                            const Icon = meta.icon;
                            const positive = tx.amount >= 0;
                            return (
                                <motion.div
                                    key={tx.tx_id}
                                    layout
                                    className="glass-card rounded-2xl p-3 sm:p-4 flex items-start gap-3 sm:gap-4"
                                    data-testid={`points-tx-${tx.tx_id}`}
                                >
                                    <div
                                        className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                                            positive ? "bg-shariti-orange/15" : "bg-red-500/15"
                                        }`}
                                    >
                                        <Icon className={`w-5 h-5 ${meta.color}`} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-bold text-white text-sm leading-snug break-words">
                                            {tx.label}
                                        </p>
                                        {/* Date and source on a SINGLE compact line; truncates on
                                            very narrow screens. Stops the previous overlap-bug. */}
                                        <p className="text-[11px] text-white/45 mt-1 flex items-center gap-1.5 min-w-0 flex-wrap">
                                            <Clock className="w-3 h-3 shrink-0" />
                                            <span className="shrink-0">
                                                {fmtDateTimeShort(tx.created_at)}
                                            </span>
                                            <span className="opacity-40 shrink-0">·</span>
                                            <span className="text-[10px] uppercase tracking-wider font-bold opacity-70">
                                                {meta.label}
                                            </span>
                                        </p>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <p
                                            className={`text-lg sm:text-xl font-extrabold tabular-nums inline-flex items-center gap-1 leading-none ${
                                                positive
                                                    ? "text-shariti-orange-light"
                                                    : "text-red-400"
                                            }`}
                                        >
                                            {positive ? (
                                                <TrendingUp className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                            ) : (
                                                <TrendingDown className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                            )}
                                            {positive ? "+" : ""}
                                            {tx.amount}
                                        </p>
                                        <p className="text-[10px] uppercase tracking-widest font-bold text-white/40 mt-0.5">
                                            pts
                                        </p>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                )}
            </div>
        </Layout>
    );
}

function FilterChip({ label, active, onClick, count, testid }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className={`px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-all inline-flex items-center gap-1.5 ${
                active
                    ? "bg-shariti-orange text-white"
                    : "text-white/60 hover:text-white"
            }`}
        >
            {label}
            <span
                className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                    active ? "bg-white/20" : "bg-white/10"
                }`}
            >
                {count}
            </span>
        </button>
    );
}
